import BackendService from 'BackendService';
import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import AIChatMessage from '../../../models/AIChatMessage';
import Contact from '../../../models/Contact';
import Investor from '../../../models/Investor';
import { VeraPromptProposal } from '../../../models/admin';
import {
  clearInvestorDraftStorage,
  ensureInvestorPreviewSynced,
  refreshPreviewGoogleSearch,
  searchContacts,
  syncGoogleSearch,
  syncGroundingPrompt,
  syncIntentPrompt,
  syncPreviewPrompt,
} from './investorSandboxApi';
import { InvestorDraft } from './veraGlobalChatPromptOverrideUtils';

export interface InvestorPromptSnapshot {
  markdown: string;
  productionMarkdown: string;
  intentText: string;
  intentProductionText: string;
  groundingText: string;
  groundingProductionText: string;
  intentTextRef: MutableRefObject<string>;
  groundingTextRef: MutableRefObject<string>;
  lastSubmittedMarkdownRef: MutableRefObject<string | null>;
  intentLastSubmittedRef: MutableRefObject<string | null>;
  groundingLastSubmittedRef: MutableRefObject<string | null>;
  userHasEditedRef: MutableRefObject<boolean>;
  sandboxSearchEnabled: boolean;
  sandboxSearchLocked: boolean;
  setMarkdown: (value: string) => void;
  setEditorContent: (value: string) => void;
  setIsDirty: (value: boolean) => void;
  setIntentText: (value: string) => void;
  setIntentIsDirty: (value: boolean) => void;
  setGroundingText: (value: string) => void;
  setGroundingIsDirty: (value: boolean) => void;
  setPromptSynced: (value: boolean) => void;
  setIntentSynced: (value: boolean) => void;
  setGroundingSynced: (value: boolean) => void;
  setSavedDraft: (draft: InvestorDraft | null) => void;
  setSandboxSearchEnabled: (enabled: boolean) => void;
  syncPromptNow: (value: string) => void;
  syncGroundingNow: (value: string) => void;
  scheduleDraftSave: (
    markdown: string,
    contact: Contact | null,
    intent?: string,
    grounding?: string,
  ) => void;
}

interface UseInvestorSandboxWorkflowOptions {
  chatIdRef: MutableRefObject<string>;
  prompts: InvestorPromptSnapshot;
  proposalId: string | undefined;
  setProductTab: (tab: number) => void;
}

export function useInvestorSandboxWorkflow({
  chatIdRef,
  prompts,
  proposalId,
  setProductTab,
}: UseInvestorSandboxWorkflowOptions) {
  const [contactSearch, setContactSearch] = useState('');
  const [contactOptions, setContactOptions] = useState<Contact[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const [contactInvestments, setContactInvestments] = useState<Investor[]>([]);
  const [selectedInvestor, setSelectedInvestor] = useState<Investor | null>(null);
  const [fundSearch, setFundSearch] = useState('');

  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedProposal, setSubmittedProposal] = useState<VeraPromptProposal | null>(null);

  const [linkedProposal, setLinkedProposal] = useState<VeraPromptProposal | null>(null);
  const [linkedProposalLoading, setLinkedProposalLoading] = useState(!!proposalId);

  useEffect(() => {
    if (contactSearch.length < 2) {
      setContactOptions([]);
      return;
    }
    setContactLoading(true);
    searchContacts(contactSearch)
      .then((results) => setContactOptions(results))
      .catch(() => setContactOptions([]))
      .finally(() => setContactLoading(false));
  }, [contactSearch]);

  useEffect(() => {
    setSelectedInvestor(null);
    setFundSearch('');
    setContactInvestments([]);
    if (!selectedContact?.id) return;
    new BackendService()
      .getContactInvestments(selectedContact.id)
      .then((investments: Investor[]) => setContactInvestments(investments))
      .catch(() => setContactInvestments([]));
  }, [selectedContact]);

  useEffect(() => {
    if (!proposalId) return;
    setLinkedProposalLoading(true);
    new BackendService()
      .getVeraProposal(proposalId)
      .then((proposal) => {
        setLinkedProposal(proposal);
        if (
          (proposal.changedPrompts ?? []).includes('admin_assist') ||
          proposal.promptType === 'admin_assist'
        ) {
          setProductTab(1);
        } else {
          setProductTab(0);
        }
      })
      .catch(() => setLinkedProposal(null))
      .finally(() => setLinkedProposalLoading(false));
  }, [proposalId, setProductTab]);

  const handleContactChange = (contact: Contact | null) => {
    setSelectedContact(contact);
    prompts.scheduleDraftSave(
      prompts.markdown,
      contact,
      prompts.intentTextRef.current,
      prompts.groundingTextRef.current,
    );
  };

  const copyProposalToEditor = () => {
    if (!linkedProposal?.systemMarkdown) return;
    prompts.userHasEditedRef.current = true;
    prompts.setMarkdown(linkedProposal.systemMarkdown);
    prompts.setIsDirty(true);
    prompts.setEditorContent(linkedProposal.systemMarkdown);
    prompts.syncPromptNow(linkedProposal.systemMarkdown);
    setLinkedProposal(null);
  };

  const handleSandboxSearchToggle = (enabled: boolean) => {
    if (prompts.sandboxSearchLocked) return;
    prompts.userHasEditedRef.current = true;
    syncGoogleSearch(chatIdRef.current, enabled)
      .then(() => {
        prompts.setSandboxSearchEnabled(enabled);
        prompts.scheduleDraftSave(
          prompts.markdown,
          selectedContact,
          prompts.intentTextRef.current,
          prompts.groundingTextRef.current,
        );
      })
      .catch(() => {
        /* non-fatal */
      });
  };

  const sendMessage = useCallback(
    async (text: string) => {
      if (!selectedContact?.id || !prompts.markdown.trim()) return;
      setIsProcessing(true);
      try {
        await ensureInvestorPreviewSynced(
          chatIdRef.current,
          prompts.markdown,
          prompts.intentTextRef.current,
          prompts.groundingTextRef.current,
          prompts.sandboxSearchEnabled,
          prompts.sandboxSearchLocked,
        );
        prompts.setPromptSynced(true);
        prompts.setIntentSynced(true);
        prompts.setGroundingSynced(true);
      } catch {
        setIsProcessing(false);
        return;
      }

      const fundName = selectedInvestor?.fundOpportunity?.name;
      const messageText = fundName ? `Regarding the fund "${fundName}": ${text}` : text;
      setMessages((prev) => [
        ...prev,
        { role: 'user', parts: [{ text: messageText }] } as AIChatMessage,
      ]);

      try {
        const result = await new BackendService().sendVeraPreviewChat(
          selectedContact.id,
          messageText,
          chatIdRef.current,
        );
        chatIdRef.current = result.chatId;
        let replyText = result.reply;
        if (result.guardrailWarning) {
          const warning = result.guardrailWarning;
          replyText +=
            `\n\n---\n⚠️ **Production guardrail — this message would have been blocked**\n\n` +
            `**Reason:** ${warning.description}\n\n` +
            `**User would have received:** "${warning.blockedReply}"`;
        }
        setMessages((prev) => [
          ...prev,
          {
            role: 'model',
            parts: [{ text: replyText }],
            timestamp: new Date(),
            googleSearchGrounding: result.googleSearchGrounding ?? null,
          } as AIChatMessage,
        ]);
      } catch (err: any) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'model',
            parts: [{ text: `Error: ${err?.message ?? 'Request failed'}` }],
          } as AIChatMessage,
        ]);
      } finally {
        setIsProcessing(false);
      }
    },
    [chatIdRef, prompts, selectedContact, selectedInvestor],
  );

  const resetChat = () => {
    chatIdRef.current = uuidv4();
    setMessages([]);
    prompts.syncPromptNow(prompts.markdown);
    prompts.syncGroundingNow(prompts.groundingTextRef.current);
    refreshPreviewGoogleSearch(chatIdRef.current)
      .then((data) => prompts.setSandboxSearchEnabled(data.sandboxEnabled))
      .catch(() => {
        /* non-fatal */
      });
  };

  const submitProposal = () => {
    setSaving(true);
    setError(null);
    setSubmittedProposal(null);
    new BackendService()
      .submitVeraCombinedProposal(
        prompts.markdown,
        prompts.intentText || prompts.intentProductionText,
        chatIdRef.current || undefined,
      )
      .then((proposal) => {
        setSubmittedProposal(proposal);
        prompts.lastSubmittedMarkdownRef.current = prompts.markdown;
        prompts.intentLastSubmittedRef.current = prompts.intentText;
        prompts.setIsDirty(false);
        prompts.setIntentIsDirty(false);
        clearInvestorDraftStorage();
        prompts.setSavedDraft(null);
      })
      .catch((err) => setError(err?.message || 'Failed to submit proposal.'))
      .finally(() => setSaving(false));
  };

  const restoreDraft = (draft: InvestorDraft) => {
    prompts.userHasEditedRef.current = true;
    prompts.setMarkdown(draft.markdown);
    prompts.setEditorContent(draft.markdown);
    prompts.setSavedDraft(null);
    prompts.syncPromptNow(draft.markdown);
    if (draft.intentText !== undefined) {
      prompts.intentTextRef.current = draft.intentText;
      prompts.setIntentText(draft.intentText);
      prompts.setIntentIsDirty(true);
      syncIntentPrompt(draft.intentText).catch(() => {
        /* non-fatal */
      });
    }
    if (draft.groundingText !== undefined) {
      prompts.groundingTextRef.current = draft.groundingText;
      prompts.setGroundingText(draft.groundingText);
      prompts.setGroundingIsDirty(true);
      prompts.syncGroundingNow(draft.groundingText);
    }
    if (draft.sandboxSearchEnabled && !prompts.sandboxSearchLocked) {
      handleSandboxSearchToggle(true);
    }
  };

  const dismissDraft = () => {
    prompts.userHasEditedRef.current = false;
    clearInvestorDraftStorage();
    prompts.setSavedDraft(null);
    if (prompts.productionMarkdown) {
      prompts.setMarkdown(prompts.productionMarkdown);
      prompts.setEditorContent(prompts.productionMarkdown);
      prompts.setIsDirty(false);
      chatIdRef.current = uuidv4();
      setMessages([]);
      prompts.syncPromptNow(prompts.productionMarkdown);
    }
    if (prompts.intentProductionText) {
      prompts.intentTextRef.current = prompts.intentProductionText;
      prompts.setIntentText(prompts.intentProductionText);
      prompts.setIntentIsDirty(false);
      prompts.intentLastSubmittedRef.current = prompts.intentProductionText;
      syncIntentPrompt(prompts.intentProductionText).catch(() => {
        /* non-fatal */
      });
    }
  };

  return {
    contactSearch,
    setContactSearch,
    contactOptions,
    contactLoading,
    selectedContact,
    setSelectedContact: handleContactChange,
    contactInvestments,
    selectedInvestor,
    setSelectedInvestor,
    fundSearch,
    setFundSearch,
    messages,
    isProcessing,
    saving,
    error,
    submittedProposal,
    linkedProposal,
    linkedProposalLoading,
    copyProposalToEditor,
    handleSandboxSearchToggle,
    sendMessage,
    resetChat,
    submitProposal,
    restoreDraft,
    dismissDraft,
  };
}
